// PURGE-RETIRED-AGENTS: the fake's plan and result of a permanent removal (services/api
// domain/org/purge.ts). Counts come from what the fake holds; the rest reads as none.
import type { OperationOutput } from "@companyos/sdk";

export type PurgePlan = OperationOutput<"digitalEmployees.purgePlan">;
export type PurgeResult = OperationOutput<"digitalEmployees.purge">;

export const removedLabel = (name: string) => `Removed agent (${name.trim().slice(0, 160)})`;

/** The plan of a DE or an AG the fake holds. */
export function fakePurgePlan(a: {
  kind: "digital_employee" | "workforce_agent";
  id: string;
  name: string;
  status: string;
  keys?: number;
  seat?: { seat_id: string; seat_title: string; holder_name: string | null } | null;
}): PurgePlan {
  const de = a.kind === "digital_employee";
  return {
    agent_kind: a.kind,
    id: a.id,
    name: a.name,
    status: a.status,
    removed_label: removedLabel(a.name),
    blocked:
      a.status === "retired"
        ? null
        : { reason: "not_retired", detail: "Retire it first: only a retired agent is removed." },
    removes: de
      ? {
          record: 1,
          seat_assignments: a.seat ? 1 : 0,
          keys: a.keys ?? 0,
          endpoints: 0,
          profile_versions: 0,
          sub_agents: 0,
          skills: 0,
          configuration: 0,
          access: 0,
          wakes: 0,
          runs: 0,
        }
      : { record: 1, profile_versions: 0, skills: 0, endpoints: 0 },
    keeps: de
      ? { messages: 0, events: 0, cost_events: 0, kpi_readings: 0, finished_runs: 0, open_tasks: 0 }
      : { events: 0, cost_events: 0 },
    tasks: { open: 0, move: 0, to: de ? (a.seat ?? null) : null },
    memory: {
      recall_agent: de,
      spaces: [],
      writes: 0,
      recall: de
        ? {
            state: "ready",
            archive: { records: 3 },
            delete: {
              counts: { spaces: 1, records: 12, copies: 0, keys: 1, grants: 2, seats: 1 },
              confirm_token: `fake-${a.id}`,
            },
          }
        : { state: "none", archive: null, delete: null },
    },
    data: { agent: { kind: a.kind, id: a.id, name: a.name, status: a.status } },
  };
}

export function fakePurgeResult(plan: PurgePlan, deleteMemory: boolean): PurgeResult {
  return {
    agent_kind: plan.agent_kind,
    id: plan.id,
    removed_label: plan.removed_label,
    removed: plan.removes,
    kept: plan.keeps,
    tasks_reassigned: plan.tasks.move,
    memory: { mode: deleteMemory ? "delete" : "archive", spaces: plan.memory.spaces.length },
  };
}

/** The production confirm rule: the name, ignoring case and a "DE · " / "AG · " prefix. */
export const sameName = (typed: string, name: string) => {
  const strip = (s: string) =>
    s
      .replace(/^(?:(?:DE|AG)\s*·\s*)+/u, "")
      .trim()
      .toLowerCase();
  return strip(typed) === strip(name);
};
